import { cn } from '@/lib/utils';

// Base skeleton atom. Use directly for one-off placeholders, or compose the
// helpers below (PanelSkeleton / TableSkeleton / ListSkeleton) for the standard
// loading shapes. Uses `bg-muted` so it themes with the rest of the app.
function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('animate-pulse rounded-md bg-muted', className)} {...props} />
  );
}

// ─── Standard composed shapes ─────────────────────────────────────────────────

interface PanelSkeletonProps {
  /** Number of field rows to render under the title. */
  rows?: number;
  className?: string;
}

// Detail-pane placeholder — a title bar plus a grid of label/value rows. Mirrors
// cards like FoSummaryCard so the swap-in is visually stable. Used by the
// FO-select fix (86d37ehfk) while the full detail loads.
function PanelSkeleton({ rows = 6, className }: PanelSkeletonProps) {
  return (
    <div className={cn('space-y-4 rounded-lg border p-4', className)}>
      <Skeleton className="h-5 w-40" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface TableSkeletonProps {
  rows?: number;
  cols?: number;
  className?: string;
}

// Table placeholder — a header row plus `rows` × `cols` cells.
function TableSkeleton({ rows = 8, cols = 4, className }: TableSkeletonProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex gap-3 border-b pb-2">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 py-1.5">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

interface ListSkeletonProps {
  rows?: number;
  className?: string;
}

// Stacked-list placeholder — `rows` two-line items (title + subtitle).
function ListSkeleton({ rows = 6, className }: ListSkeletonProps) {
  return (
    <div className={cn('space-y-3', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-1.5">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export { Skeleton, PanelSkeleton, TableSkeleton, ListSkeleton };
